import { useRef, useState, useCallback } from "react";

export interface SmartPositionConfig {
  width?: number;
  height?: number;
  offset?: number;
  preferredPosition?: "bottom-left" | "bottom-right" | "top-left" | "top-right";
}

export const useSmartPosition = (config: SmartPositionConfig = {}) => {
  const {
    width = 180,
    height = 120,
    offset = 8,
    preferredPosition = "bottom-left"
  } = config;

  const elementRef = useRef<HTMLElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const calculatePosition = useCallback(() => {
    if (!elementRef.current) return;

    const rect = elementRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    let top = 0;
    let left = 0;

    // Calculate based on preferred position
    switch (preferredPosition) {
      case "bottom-left":
        top = rect.bottom + offset;
        left = rect.right - width;
        break;
      case "bottom-right":
        top = rect.bottom + offset;
        left = rect.left;
        break;
      case "top-left":
        top = rect.top - height - offset;
        left = rect.right - width;
        break;
      case "top-right":
        top = rect.top - height - offset;
        left = rect.left;
        break;
    }

    // Boundary detection and adjustment
    const padding = 10;

    // Horizontal overflow check
    if (left + width > viewport.width - padding) {
      // Move to left side of element
      left = rect.left - width - offset;
    }
    if (left < padding) {
      // Align with element's left or right edge
      left = preferredPosition.includes("left")
        ? rect.left
        : rect.right - width;
    }
    // Ensure minimum padding
    left = Math.max(padding, Math.min(left, viewport.width - width - padding));

    // Vertical overflow check
    if (top + height > viewport.height - padding) {
      // Position above element
      top = rect.top - height - offset;
    }
    if (top < padding) {
      // Position below element
      top = rect.bottom + offset;
    }
    // Ensure minimum padding
    top = Math.max(padding, Math.min(top, viewport.height - height - padding));

    setPosition({ top, left });
  }, [width, height, offset, preferredPosition]);

  return { elementRef, position, calculatePosition };
};
